import React, { useState, useEffect, useRef } from 'react';
import Decimal from 'decimal.js';
import { useMoralis } from 'react-moralis';
import {
  VStack,
  useDisclosure,
  Box,
  Button,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Collapse,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  Link as CLink
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import PnLCalculator from './TradingBots/PNL';

// ===================== CONFIG / CONSTANTS =====================
const MORALIS_API_KEY =
  (Math.random() < 0.5
    ? process.env.REACT_APP_MORALIS_API_KEY
    : process.env.REACT_APP_MORALIS_API_KEY_2) ||
  process.env.REACT_APP_MORALIS_API_KEY;
const EXPLORER_BASE = "https://explorer-api.cronos.org/mainnet/api/v2"; // used only for $BUTT gate
const BUTT_CONTRACT   = "0x2dfA528939D38a3654ad44a4748BE591e283a32f";

// Pricing path constants
const USDC = "0xc21223249CA28397B4B6541dfFaEcC539BfF0c59";
const WCRO = "0x5C7F8A570d578ED84E63fdFA7b1eE72dEae1AE23";

// Some common Cronos DEX routers (UniswapV2-style)
const ROUTERS = [
  // VVS
  "0x145863Eb42Cf62847A6Ca784e6416C1682b1b2Ae",
  // MM Finance (Meerkat old)
  "0x145677FC4d9b8F19B5D56d1820c48e0443049a30",
];

// CORS-friendly public Cronos RPCs (rotate on failures)
const CRONOS_RPCS = [
  "https://cronos-evm.publicnode.com",
  "https://cronos.drpc.org",
  "https://cronos-mainnet.public.blastapi.io",
  "https://evm.cronos.org" // last (often CORS-hostile)
];

// ABIs
const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)"
];
const UNIV2_ROUTER_ABI = [
  "function getAmountsOut(uint amountIn, address[] calldata path) external view returns (uint[] memory amounts)"
];

// ===================== PROVIDER w/ FALLBACK =====================
function useCronosProvider() {
  const idxRef = useRef(0);
  const providerRef = useRef(new ethers.providers.JsonRpcProvider(CRONOS_RPCS[0]));

  const rotate = () => {
    idxRef.current = Math.min(idxRef.current + 1, CRONOS_RPCS.length - 1);
    providerRef.current = new ethers.providers.JsonRpcProvider(CRONOS_RPCS[idxRef.current]);
    return providerRef.current;
  };

  const get = () => providerRef.current;

  const tryWithProviderFallback = async (fn) => {
    let lastErr;
    for (let i = idxRef.current; i < CRONOS_RPCS.length; i++) {
      try {
        return await fn(providerRef.current);
      } catch (e) {
        lastErr = e;
        if (i < CRONOS_RPCS.length - 1) rotate();
      }
    }
    throw lastErr;
  };

  return { getProvider: get, tryWithProviderFallback };
}

// ===================== HELPERS =====================
// Moralis (NEW): /wallets/{address}/tokens
async function fetchMoralisTokens(address) {
  if (!MORALIS_API_KEY) throw new Error("Missing REACT_APP_MORALIS_API_KEY");

  const base = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/tokens`;
  const headers = {
    accept: "application/json",
    "X-API-Key": MORALIS_API_KEY,
  };

  const candidates = [
    `${base}?chain=cronos&exclude_spam=true&limit=100`,
    `${base}?chain=0x19&exclude_spam=true&limit=100`,
  ];

  for (const url of candidates) {
    const r = await fetch(url, { method: "GET", headers });
    if (r.ok) {
      const j = await r.json();
      // This endpoint typically returns { result: [...], cursor?: string, page_size?: number }
      if (Array.isArray(j)) return j; // just-in-case shape
      if (Array.isArray(j?.result)) return j.result;
      return []; // defensive
    }
    // if 4xx, bubble early
    if (r.status < 500) {
      const txt = await r.text().catch(() => "");
      throw new Error(`Moralis ${r.status}: ${txt}`);
    }
  }

  // last-try, smaller page
  const r = await fetch(`${base}?chain=0x19&exclude_spam=true&limit=50`, { method: "GET", headers });
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(`Moralis ${r.status}: ${txt}`);
  }
  const j = await r.json();
  if (Array.isArray(j)) return j;
  if (Array.isArray(j?.result)) return j.result;
  return [];
}

// BUTT balance via explorer (for gate)
async function fetchExplorerTokenBalance(address, contract) {
  const url = `${EXPLORER_BASE}?module=account&action=tokenbalance&contractaddress=${contract}&address=${address}&tag=latest`;
  const res = await fetch(url);
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Explorer tokenbalance HTTP ${res.status} ${txt}`);
  }
  return res.json(); // { status, message, result: "balance" }
}

// ethers helpers
async function getTokenDecimals(provider, tokenAddress) {
  try {
    const c = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const d = await c.decimals();
    return Number(d);
  } catch {
    return 18;
  }
}
async function quoteOnRouter(provider, routerAddr, amountIn, path) {
  const router = new ethers.Contract(routerAddr, UNIV2_ROUTER_ABI, provider);
  const amounts = await router.getAmountsOut(amountIn, path);
  return amounts[amounts.length - 1];
}
async function getUsdPriceFromRouters(tryWithProviderFallback, tokenAddress, tokenDecimals) {
  const t = tokenAddress.toLowerCase();
  if (t === USDC.toLowerCase()) return 1;

  const paths = [];
  const amountIn = ethers.utils.parseUnits("1", tokenDecimals);
  if (t === WCRO.toLowerCase()) {
    paths.push([WCRO, USDC]);
  } else {
    paths.push([tokenAddress, USDC]);          // direct
    paths.push([tokenAddress, WCRO, USDC]);    // via WCRO
  }

  for (const path of paths) {
    for (const r of ROUTERS) {
      try {
        const out = await tryWithProviderFallback(async (prov) => quoteOnRouter(prov, r, amountIn, path));
        if (out && out.gt(0)) return Number(ethers.utils.formatUnits(out, 6)); // USDC(6)
      } catch {
        // try next router/provider
      }
    }
  }
  return 0;
}
// tolerate scientific notation and huge ints
function toFormattedBalance(raw, decimals) {
  if (raw == null) return 0;
  try {
    const d = new Decimal(raw.toString());
    const div = new Decimal(10).pow(decimals || 18);
    return Number(d.div(div).toString());
  } catch {
    return 0;
  }
}

// ===================== COMPONENT =====================
const App = () => {
  const [wallets, setWallets] = useState(['']);
  const { account } = useMoralis();
  const [address, setAddress] = useState('');
  const [assets, setAssets] = useState([]);

  const [buttonClicked, setButtonClicked] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTokenAddress, setSelectedTokenAddress] = useState(null);
  const [selectedTokenName, setSelectedTokenName] = useState("");

  const [realizedPnL, setRealizedPnL] = useState(Decimal(0));
  const [unrealizedPnL, setUnrealizedPnL] = useState(Decimal(0));
  const [totalPnL, setTotalPnL] = useState(Decimal(0));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalBuys, setTotalBuys] = useState(Decimal(0));
  const [totalSells, setTotalSells] = useState(Decimal(0));
  const [loading, setLoading] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [showBalances, setShowBalances] = useState(true);
  const [AverageCostState, setAverageCostState] = useState(0);
  const [AverageSellState, setAverageSellState] = useState(0);
  const [recommendation, setRecommendation] = useState("");
  const [buttBalance, setButtBalance] = useState(0);
  const [croPrice, setCroPrice] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getProvider, tryWithProviderFallback } = useCronosProvider();

  // CRO price (remove if you want 0 external quotes)
  useEffect(() => {
    const fetchPrice = () => {
      fetch('https://api.coingecko.com/api/v3/simple/price?ids=crypto-com-chain&vs_currencies=usd')
        .then(res => res.json())
        .then(data => setCroPrice(data['crypto-com-chain']?.usd))
        .catch(() => {});
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 100000);
    return () => clearInterval(interval);
  }, []);

  // Load / persist wallets
  useEffect(() => {
    const WalletsfromStorage1 = localStorage.getItem('walletsStorage');
    try {
      if (WalletsfromStorage1) {
        const parsed = JSON.parse(WalletsfromStorage1);
        setTimeout(() => { if (parsed) setWallets(parsed); }, 500);
      }
    } catch {}
  }, []);
  useEffect(() => {
    if (wallets && wallets.length > 0 && wallets[0] !== '' ){
      localStorage.setItem('walletsStorage', JSON.stringify(wallets));
    }
  }, [wallets]);

  // Wallet add/merge logic
  useEffect(() => {
    if (account && !address && !wallets.includes(account) || (buttonClicked && wallets.length === 0)) {
      setWallets([account || '']);
    } else if (wallets.length > 0 && wallets[0] === "" && !account && address && !wallets.includes(address) && buttonClicked) {
      setWallets([address]);
    } else if (wallets.length > 0 && wallets[0].includes("0x") && !account && address && !wallets.includes(address) && buttonClicked) {
      setWallets([...wallets, address]);
    } else if (account && address && !wallets.includes(account)) {
      setWallets([...wallets, account]);
    } else if (account && address && !wallets.includes(address)) {
      setWallets([...wallets, address]);
    }
  }, [account, buttonClicked, address, wallets]);

  // Fetch assets (Moralis new endpoint) + ethers pricing
  useEffect(() => {
    if (MORALIS_API_KEY && wallets.length > 0 && (address || account || wallets !== '' ) ) {
      fetchAssets();
    }
  }, [account, buttonClicked, wallets]);

// Replace your existing fetchAssets with this one
const fetchAssets = async () => {
  if (!wallets || wallets.length === 0 || wallets.every((w) => !w)) {
    console.warn("No valid wallets available for fetching assets.");
    return;
  }

  const MORALIS_API_KEY = process.env.REACT_APP_MORALIS_API_KEY || "";
  if (!MORALIS_API_KEY) {
    console.warn("Missing REACT_APP_MORALIS_API_KEY");
    return;
  }

  // Known wrapped CRO contract(s) on Cronos (lowercased)
  const WCRO_ADDRESSES = [
    "0x5c7f8a570d578ed84e63fdfa7b1ee72deae1ae23" // WCRO
  ];

  // Safe formatter that keeps precision (Moralis returns balance as a string)
  const formatUnitsDecimal = (rawStr, decimals) => {
    try {
      const d = new Decimal(rawStr || "0");
      const scale = new Decimal(10).pow(decimals || 0);
      return d.div(scale).toNumber();
    } catch {
      return 0;
    }
  };

  try {
    const perWalletArrays = await Promise.all(
      wallets
        .filter(Boolean)
        .map(async (wallet) => {
          const url = `https://deep-index.moralis.io/api/v2.2/wallets/${wallet}/tokens?chain=cronos&exclude_spam=true&limit=100`;
          const res = await fetch(url, {
            method: "GET",
            headers: {
              accept: "application/json",
              "X-API-Key": MORALIS_API_KEY,
            },
          });

          if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(`Moralis ${res.status}: ${txt}`);
          }

          // Moralis returns { result: [...] }
          const data = await res.json();
          const list = Array.isArray(data?.result) ? data.result : [];

          // Normalize each token row, compute CRO/WCRO value from croPrice
          const normalized = list.map((t) => {
            const token_address =
              (t.token_address || t.address || "").toLowerCase();
            const symbol = (t.symbol || "").toUpperCase();
            const name = t.name || t.symbol || "";
            const logo = t.logo || null;
            const decimals = Number(t.decimals ?? 18);

            // Use Moralis balance string directly; avoid Number() to prevent scientific notation
            const rawBalance = String(t.balance ?? "0");
            const balance_formatted =
              t.balance_formatted != null
                ? Number(t.balance_formatted)
                : formatUnitsDecimal(rawBalance, decimals);

            // Start with Moralis pricing if present (some may be 0)
            let usd_price = Number(t.usd_price || 0);
            let usd_value =
              t.usd_value != null
                ? Number(t.usd_value)
                : balance_formatted * usd_price;

            // If it's CRO or WCRO, override with live croPrice when available
            const looksLikeCro =
              symbol === "CRO" ||
              WCRO_ADDRESSES.includes(token_address || "");
            if (looksLikeCro && croPrice && balance_formatted > 0) {
              usd_price = Number(croPrice);
              usd_value = balance_formatted * Number(croPrice);
            }

            return {
              token_address,
              symbol: t.symbol || "",
              name,
              logo,
              decimals,
              balance: rawBalance, // keep raw string for precision
              balance_formatted,
              usd_price,
              usd_value,
              usd_price_24hr_percent_change: Number(
                t.usd_price_24hr_percent_change || 0
              ),
            };
          });

          return normalized;
        })
    );

    // Merge same tokens across wallets by token_address (fallback to name)
    const merged = perWalletArrays.flat().reduce((acc, it) => {
      const key = it.token_address || it.name;
      const idx = acc.findIndex(
        (a) => (a.token_address || a.name) === key
      );
      if (idx !== -1) {
        const prev = acc[idx];
        acc[idx] = {
          ...prev,
          balance_formatted:
            (prev.balance_formatted || 0) + (it.balance_formatted || 0),
          // keep one raw balance as string isn't sum-friendly; we merge formatted + USD
          usd_value: (prev.usd_value || 0) + (it.usd_value || 0),
          // Prefer a non-zero price if previous was zero
          usd_price: prev.usd_price || it.usd_price,
          // Fill any missing visuals/labels
          logo: prev.logo || it.logo,
          symbol: prev.symbol || it.symbol,
          name: prev.name || it.name,
          decimals: prev.decimals ?? it.decimals,
        };
      } else {
        acc.push({ ...it });
      }
      return acc;
    }, []);

    setAssets(merged);
  } catch (error) {
    console.error("Error fetching assets (Moralis):", error);
  }
};


  // Sorting
  useEffect(() => {
    if (sortBy) {
      const sorted = [...assets].sort((a, b) => {
        const av = a[sortBy] ?? 0;
        const bv = b[sortBy] ?? 0;
        if (av < bv) return sortDirection === 'asc' ? -1 : 1;
        if (av > bv) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      setAssets(sorted);
    }
  }, [sortBy, sortDirection, assets]);

  // UI handlers
  const handleInputChange = (e) => {
    if (buttonClicked){
      setAddress(e.target.value);
      setWallets([...wallets, address])
    } else {
      setAddress(e.target.value);
    }
  };
  const handleButtonClick = () => {
    if (address && !wallets.includes(address)) {
      setWallets(prev => [...prev, address]);
      setAddress('');
    }
    setButtonClicked(true);
    setTimeout(() => setButtonClicked(false), 1000);
  };
  const handleRemoveWallet = (walletToRemove) => {
    const updated = wallets.filter(w => w !== walletToRemove);
    setWallets(updated);
    if (updated.length === 0) {
      setAssets([]);
    } else {
      setTimeout(() => { fetchAssets(); }, 0);
    }
  };

  const calculateTotalValueUSD = () => assets.reduce((t, a) => t + (a.usd_value || 0), 0).toFixed(2);
  const handleSort = (columnName) => {
    if (sortBy === columnName) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(columnName);
      setSortDirection('asc');
    }
  };
  const sortingOptions = [
    { label: 'Name', value: 'name' },
    { label: 'Value (USD)', value: 'usd_value' },
    { label: '24h Change', value: 'usd_price_24hr_percent_change' },
  ];
  const getActiveSortLabel = () => (sortingOptions.find(o => o.value === sortBy)?.label || 'Sort By');
  const calculateDailyChangeInUSD = (asset) => {
    if (!asset) return "0.00";
    const pct = asset.usd_price_24hr_percent_change || 0;
    const val = asset.usd_value || 0;
    const prev = val / (1 + (pct / 100));
    return (val - prev).toFixed(2);
  };
  const calculateDailyTotalChangeInUSD = () =>
    assets.reduce((total, a) => total + parseFloat(calculateDailyChangeInUSD(a)), 0).toFixed(2);
  const handleToggle = () => setShowDetails(!showDetails);

  useEffect(() => {
    const walletDiv = document.getElementById('Portfolio');
    if (walletDiv) walletDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // $BUTT gate via explorer
   useEffect(() => {
     if (account && account.includes("0x")) {
       const API_KEY_CRONOSCAN = "V0B7jKfZaBhjDjmexQSkzRPWTcNv16bF";
       const getTokenBalance = async (Address) => {
         try {
 // New base (mainnet): https://explorer-api.cronos.org/mainnet/api/v2
 const apiUrl = `https://explorer-api.cronos.org/mainnet/api/v2?module=account&action=tokenbalance&contractaddress=0x2dfA528939D38a3654ad44a4748BE591e283a32f&address=${Address}${API_KEY_CRONOSCAN ? `&apikey=${API_KEY_CRONOSCAN}` : ''}`;
           const response = await fetch(apiUrl);
           const data = await response.json();
           if (data.status === "1") {

             setButtBalance(Number(data.result));
             console.log(data.result, "= Butt Balance");
           } else {
             console.error("Failed to fetch token balance");
           }
         } catch (error) {
           console.error("Error fetching token balance:", error.message);
         }
       };
 
       getTokenBalance(account);
     }
   }, [account]);

  return (
    <Box
      border="1px solid"
      borderColor={'white'}
      borderRadius="2xl"
      p={6}
      boxShadow="xl"
      backdropFilter="blur(12px)"
      padding={'0.3vw'}
      backgroundColor="rgba(0, 0, 0, 0.8)"
      color={'white'}
      fontSize='2vmax'
      alignItems={'center'}
      justifyItems={'center'}
      textAlign={'center'}
    >
      <Text color='yellow' textShadow='5px 0px 5px red' padding={'2vw'} id='Portfolio' textAlign={'center'} fontSize={'2vmax'} mb="4">
        Cronos-CHAIN Portfolio tracker
      </Text>
      <Text color='yellow' textShadow='5px 0px 5px red' id='Portfolio' textAlign={'center'} fontSize={'1vmax'} mb="4">
        $CRO current price: {croPrice ? `${croPrice.toFixed(3)} $` : "..."}
      </Text>

      <Box className="portfolio-controls" mb="4">
        <Input
          type="text"
          value={address}
          onChange={handleInputChange}
          placeholder="Add Wallets"
          fontSize={'1.5vmax'}
          mr="1"
          width={'50%'}
          boxSize={'3,3'}
        />
        <Button fontSize={'1.5vmax'} colorScheme="orange" width='fit-content' onClick={handleButtonClick}>
          Save wallet and Fetch Assets
        </Button>
      </Box>

      <Box paddingBottom={'5vmin'} fontSize={'1vmax'} className="wallets" >
        <Text justifySelf={'center'} textAlign={'CENTER'} width={'fit-content'} color='white' fontSize={'2VMAX'}>
          Tracked Wallets
        </Text>
        <Button fontSize={'1.5vmax'} bgColor='black' color="orangered" variant="outline" width={'full'} onClick={() => setShowWallets(!showWallets)}>
          {showWallets ? 'Hide wallets' : 'Show wallets'}
        </Button>
        <Collapse in={showWallets}>
          <ul style={{ rowGap: '1px', height: 'fit-content' }}>
            {wallets.map((wallet, index) => (
              <li style={{ padding: '1px', height: 'fit-content' }} key={index}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CLink
                    href={`https://explorer.cronos.org/address/${wallet}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="orangered"
                    _hover={{ textDecoration: 'underline', color: 'white' }}
                    textShadow="0 0 3px white"
                    mr={4}
                  >
                    {wallet}
                  </CLink>
                  <Button size="xs" colorScheme="red" onClick={() => handleRemoveWallet(wallet)}>
                    Remove
                  </Button>
                </Box>
              </li>
            ))}
          </ul>
        </Collapse>

        <Text
          position={'sticky'}
          top='0'
          alignSelf={'center'}
          padding={'2vmax'}
          margin={'1vmax'}
          textShadow='0 0 30px orangered'
          width={'full'}
          height={'3.0vmax'}
          variant="outline"
          as={Text}
          color='white'
          mt={6}
          fontSize={'1.5vmax'}
        >
          Total value :{" "}
          <Text as="span" style={{ textUnderlineOffset:'1px' ,color:'green', padding:'2vmax', fontSize:'2.5vmax' }}>
            {showBalances ? `${calculateTotalValueUSD()}` : "$$$"}
            <Text
              as="span"
              style={{ textUnderlineOffset:'1px', padding:'2vmax', fontSize:'1.5vmax' }}
              className={Number(calculateDailyTotalChangeInUSD()) < 0 ? 'negative' : 'positive'}
            >
              {showBalances ? `${Number(calculateDailyTotalChangeInUSD()) < 0 ? "" : '+'}${calculateDailyTotalChangeInUSD()}$  / 24hrs` : " + $$$ / 24hrs"}
            </Text>
          </Text>
        </Text>

        <Text
          position={'sticky'}
          top='0'
          alignSelf={'center'}
          padding={'2vmax'}
          margin={'1vmax'}
          textShadow='0 0 30px orangered'
          width={'full'}
          height={'3.0vmax'}
          variant="outline"
          as={Text}
          color='white'
          mt={6}
          fontSize={'1.5vmax'}
        >
          Total value in CRO :{" "}
          <Text as="span" style={{ textUnderlineOffset:'1px' ,color:'yellow', fontSize:'1.5vmax' }}>
            {showBalances ? `${croPrice ? (Number(calculateTotalValueUSD())/croPrice).toFixed(2) : '...' } $CRO` : "$$$"}
          </Text>
        </Text>

        <Button fontSize={'1.5vmax'} bgColor='black' color="orangered" variant="outline" width={'full'} onClick={() => setShowBalances(!showBalances)}>
          {showBalances ? 'Hide Balances' : 'Show Balances'}
        </Button>

        <Text> click token worth ($$$) for PNL (2 million $BUTT required)</Text>
        
      </Box>

      <Box content="contain" width="100%" overflow="hidden" className="nft-cardswallet">
        <Menu width='full' justifyItems='center'>
          <Text fontSize={'1.5vmax'}>SORT BY:</Text>
          <MenuButton justifySelf={'center'} paddingLeft={'2vw'} as={Button} rightIcon={<ChevronDownIcon />} bg="orangered" color="white" _hover={{ bg: 'white', color: 'orangered' }}>
            {getActiveSortLabel()}
          </MenuButton>
          <MenuList bg="#0a1b2f" color="white">
            {sortingOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleSort(option.value)}
                bg={sortBy === option.value ? 'orangered' : 'transparent'}
                _hover={{ bg: 'white', color: 'orangered' }}
                fontSize={'1.5vmax'}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        <Table>
          <Thead transform="scale(0.5)" id="asset-table"></Thead>
          {assets.map((asset) =>
            (asset.usd_value || 0) > 0 || (asset.balance_formatted || 0) > 0 ? (
              <Tbody key={asset.token_address}>
                <Tr transform="scale(0.85)" id="assets" paddingTop="2vmax" rowGap="1px" fontSize="1.8vmax">
                  <Td
                    textAlign="center"
                    maxWidth="10px"
                    lineHeight="2.1vmax"
                    rowGap="1px"
                    paddingTop="1.8vmax"
                    borderBottom="hidden"
                    fontSize="2.0vmax"
                    gap={"1"}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {asset.logo ? (
                        <img
                          style={{ borderRadius: '50%', width: '2vmax', marginRight: '0.5vmax' }}
                          src={asset.logo}
                          alt={`${asset.symbol} logo`}
                        />
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            width: '2vmax',
                            height: '2vmax',
                            marginRight: '0.5vmax',
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            color: '#fff',
                            textAlign: 'center',
                            lineHeight: '2vmax',
                            fontSize: '1.5vmax',
                            fontWeight: 'bold'
                          }}
                        >
                          $
                        </span>
                      )}
                      {(asset.symbol || "").slice(0, 20)} {(asset.symbol || "").length > 20 ? '...' : ''}
                    </div>
                  </Td>

                  <Td textAlign="center" paddingTop="2vmax" color="gold" borderBottom="hidden" fontSize="2vmax">
                    <Box>
                      <Button
                        textAlign="center"
                        padding={'2vmin'}
                        color="gold"
                        borderBottom="hidden"
                        fontSize="2vmax"
                        bg={'transparent'}
                        textShadow={'5px 0px 5px black'}
                        textDecoration={'underline'}
                        onClick={() => {
                          setSelectedTokenAddress(asset.token_address);
                          setSelectedTokenName(asset.name);
                          setIsModalOpen(true);
                        }}
                      >
                        {showBalances ? `$ ${parseFloat(((asset.usd_value || 0)).toFixed(2))}` : "$$$"}
                      </Button>

                      <Button
                        hidden={account !== "0x7081d3fb56f71868ad55780582bc37bbc536e6d6" && account !== "0x951d187cb9fe0a6aa7052a583c15dcbed5a0b7d7" && account !== '0x8E3D43bC8D933542E6caeCEce117E5b608202374'}
                        as={Link}
                        to={`/donotuse?token=${asset.token_address}`}
                        fontSize="0.8vmax"
                        fontWeight="bold"
                        padding={'0.2vmax'}
                        width="45%"
                        height="40px"
                        justifyContent="center"
                        alignItems="center"
                        textAlign="center"
                        color={'red'}
                        background={'black'}
                        _hover={{ textDecoration: 'underline', color: 'black', backgroundColor: 'red' }}
                      >
                        TRADING BOT
                      </Button>
                    </Box>
                  </Td>

                  <Td
                    rowGap="1vmax"
                    textAlign="center"
                    paddingTop="2vmax"
                    borderBottom="hidden"
                    fontSize="1.5vmax"
                    className={(asset.usd_price_24hr_percent_change || 0) < 0 ? 'negative' : 'positive'}
                  >
                    {parseFloat((asset.usd_price_24hr_percent_change || 0).toFixed(2))}% ={' '}
                    {showBalances ? `${calculateDailyChangeInUSD(asset)}` : "$$$"}
                  </Td>
                </Tr>

                <Tr transform="scale(0.85)" rowGap="0px">
                  <Td textAlign="center" borderBottom="2px outset grey" fontSize="1.5vmax">
                    <CLink
                      lineHeight="2vmax"
                      textAlign="center"
                      textColor="orangered"
                      href={`https://dexscreener.com/cronos/${asset.token_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {(asset.name || "").slice(0, 10)} {(asset.name || "").length > 10 ? '...' : ''} 📈
                    </CLink>
                  </Td>
                  <Td textAlign="center" borderBottom="2px outset grey" fontSize="1.5vmax">
                    {showBalances ? `${parseFloat(((asset.balance_formatted || 0)).toFixed(2))}` : "................."}
                  </Td>
                  <Td textAlign="center" borderBottom="2px outset grey" fontSize="1.5vmax">
                    ${parseFloat(((asset.usd_price || 0).toFixed(9)))}
                  </Td>
                </Tr>
              </Tbody>
            ) : null
          )}
        </Table>
       {selectedTokenAddress && (
          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <ModalOverlay />
            <ModalContent maxWidth="fit-content" bg="blackAlpha.900">
              <ModalHeader textColor="white" fontSize="1.5vmax">{selectedTokenName} PNL</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {loading ? (
                  <Box textAlign="center" p="4">
                    <Text color="white">Calculating PnL...</Text>
                    <Box mt="4" display="flex" justifyContent="center" alignItems="center">
                      <Spinner color="orange.500" size="lg" />
                    </Box>
                  </Box>
                ) : buttBalance < 2000000 ? (
                  <Box textAlign="center" p="4">
                    <Text color="white">Not enough $BUTT</Text>
                    <Box mt="4" display="flex" justifyContent="center" alignItems="center" />
                  </Box>
                ) : (
       <PnLCalculator/>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
        {/* Your existing PnL modal & logic can stay as-is, since usd_price is now populated on-chain */}
      </Box>
    </Box>
  );
};

export default App;
